import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import Slider from 'react-slick';
import { useIntl } from 'react-intl';
import styles from './WhatVivianlabOffers.module.css';

import preview1 from '../Images/VivianLabOfferingLogos/AiAssitant.png';
import preview2 from '../Images/VivianLabOfferingLogos/coaching.png';
import preview3 from '../Images/VivianLabOfferingLogos/tracker.png';
import preview4 from '../Images/VivianLabOfferingLogos/events.png';
import preview5 from '../Images/VivianLabOfferingLogos/slide2.png';

import preview1El from '../Images/VivianLabOfferingLogos/AiAssitant-el.png';
import preview2El from '../Images/VivianLabOfferingLogos/coaching-el.png';
import preview3El from '../Images/VivianLabOfferingLogos/tracker-el.png';
import preview4El from '../Images/VivianLabOfferingLogos/events-el.png';
import preview5El from '../Images/VivianLabOfferingLogos/slide2-el.png';
import { getStrapiLocale } from '../../../util/genericHelpers';

function WhatVivianlabOffers() {
  const intl = useIntl();
  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const locale = getStrapiLocale() || 'el-GR';
  const isEnglish = String(locale)
    .toLowerCase()
    .startsWith('en');
  const isGreek = String(locale)
    .toLowerCase()
    .startsWith('el');
  const previews = isGreek
    ? [preview1El, preview2El, preview3El, preview4El, preview5El]
    : [preview1, preview2, preview3, preview4, preview5];
  const sectionTitle = intl.formatMessage({ id: 'WhatVivianlabOffers.title' });
  const sectionSubtitle = intl.formatMessage({ id: 'WhatVivianlabOffers.subtitle' });

  const splitDescription = text => {
    const parts = String(text || '').split('\n');
    return {
      subtitle: parts[0] || '',
      description: parts.slice(1).join('\n'),
    };
  };

  const items = useMemo(
    () => [
      {
        id: 'ai-assistant',
        title: intl.formatMessage({ id: 'WhatVivianlabOffers.item1.title' }),
        ...splitDescription(intl.formatMessage({ id: 'WhatVivianlabOffers.item1.description' })),
        image: previews[0],
      },
      {
        id: 'personalized-daily-coaching',
        title: intl.formatMessage({ id: 'WhatVivianlabOffers.item2.title' }),
        ...splitDescription(intl.formatMessage({ id: 'WhatVivianlabOffers.item2.description' })),
        image: previews[1],
      },
      {
        id: 'symptom-mood-tracker',
        title: intl.formatMessage({ id: 'WhatVivianlabOffers.item3.title' }),
        ...splitDescription(intl.formatMessage({ id: 'WhatVivianlabOffers.item3.description' })),
        image: previews[2],
      },
      {
        id: 'live-expert-webinars',
        title: intl.formatMessage({ id: 'WhatVivianlabOffers.item4.title' }),
        ...splitDescription(intl.formatMessage({ id: 'WhatVivianlabOffers.item4.description' })),
        image: previews[3],
      },
      {
        id: 'educational-content',
        title: intl.formatMessage({ id: 'WhatVivianlabOffers.item5.title' }),
        ...splitDescription(intl.formatMessage({ id: 'WhatVivianlabOffers.item5.description' })),
        image: previews[4],
      },
    ],
    [intl, previews]
  );

  const visibleItems = useMemo(
    () => items.filter(item => !(isEnglish && item.id === 'live-expert-webinars')),
    [items, isEnglish]
  );

  useEffect(() => {
    if (!visibleItems.length || activeIndex < visibleItems.length) return;

    const nextIndex = visibleItems.length - 1;
    setActiveIndex(nextIndex);

    const slider = sliderRef.current;
    if (!slider || typeof slider.slickGoTo !== 'function') return;
    slider.slickGoTo(nextIndex);
  }, [activeIndex, visibleItems.length]);

  const normalizeIndex = useCallback(
    index => {
      const total = visibleItems.length;
      if (!total || typeof index !== 'number' || Number.isNaN(index)) {
        return 0;
      }

      return ((index % total) + total) % total;
    },
    [visibleItems.length]
  );

  const goToSlide = useCallback(
    index => {
      const targetIndex = normalizeIndex(index);
      setActiveIndex(targetIndex);

      const slider = sliderRef.current;
      if (!slider || typeof slider.slickGoTo !== 'function') return;
      slider.slickGoTo(targetIndex);
    },
    [normalizeIndex]
  );

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 450,
    cssEase: 'ease',
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
    swipeToSlide: false,
    draggable: true,
    touchThreshold: 12,
    adaptiveHeight: true,
    afterChange: current => {
      if (typeof current === 'number') {
        setActiveIndex(normalizeIndex(current));
      }
    },
    focusOnSelect: false,
    responsive: [
      {
        breakpoint: 901,
        settings: {
          infinite: true,
        },
      },
    ],
  };

  return (
    <section className={styles.offersSection}>
      <header className={styles.header}>
        <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: sectionTitle }} />
        <p className={styles.subtitle}>{sectionSubtitle}</p>
      </header>

      <div className={styles.layout}>
        {/* Screenshot preview */}
        <div className={styles.preview}>
          <Slider ref={sliderRef} {...sliderSettings}>
            {visibleItems.map(item => (
              <div key={item.id} className={styles.slide}>
                <img src={item.image} alt={item.title} className={styles.image} />
              </div>
            ))}
          </Slider>
          <div className={styles.dots} aria-label="Carousel pagination">
            {visibleItems.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={`${item.id}-dot`}
                  type="button"
                  className={`${styles.dotButton} ${isActive ? styles.dotButtonActive : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <span className={styles.dotVisual} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {visibleItems.map((item, index) => {
            const isActive = index === activeIndex;

            return (
              <button
                key={item.id}
                type="button"
                className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                onClick={() => goToSlide(index)}
                onMouseDown={e => e.preventDefault()}
              >
                <div className={styles.tabHeader}>
                  <span className={styles.tabTitle}>{item.title}</span>
                  {/* <span className={`${styles.pill} ${isActive ? styles.pillActive : ''}`}>
                    {isActive
                      ? intl.formatMessage({ id: 'WhatVivianlabOffers.activeLabel' })
                      : intl.formatMessage({ id: 'WhatVivianlabOffers.viewLabel' })}
                  </span> */}
                </div>
                <h4 className={`${styles.tabSubTitle} ${!isActive ? styles.tabSubTitleMuted : ''}`}>
                  {item.subtitle}
                </h4>
                {isActive && <p className={styles.tabDescription}>{item.description}</p>}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default WhatVivianlabOffers;
